import { useCurrency } from '../../context/CurrencyContext';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, X, Send, FileText, CheckCircle, Clock, AlertCircle, Trash2, Pencil, Check, Download, Mail } from 'lucide-react';
import api from '../../api/client';

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-4">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

const statusConfig = {
  draft: { color: 'bg-gray-100 text-gray-600', icon: FileText, label: 'Draft' },
  pending_signature: { color: 'bg-amber-50 text-amber-600', icon: Clock, label: 'Pending Signature' },
  active: { color: 'bg-emerald-50 text-emerald-600', icon: CheckCircle, label: 'Active' },
  expired: { color: 'bg-red-50 text-red-600', icon: AlertCircle, label: 'Expired' },
  terminated: { color: 'bg-gray-200 text-gray-500', icon: AlertCircle, label: 'Terminated' },
};

export default function AgreementsTab() {
  const [agreements, setAgreements] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ storeId: '', agreedPercentage: 10, validityPeriodMonths: 6, merchantEmail: '' });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [editingEmail, setEditingEmail] = useState(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const load = async () => {
    const params = filterStatus ? `?status=${filterStatus}` : '';
    const [aRes, sRes] = await Promise.all([
      api.get(`/api/config/admin/agreements${params}`).catch(() => ({ data: [] })),
      api.get('/api/admin/stores').catch(() => ({ data: [] })),
    ]);
    setAgreements(aRes.data);
    setStores(sRes.data.filter(s => s.is_active));
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterStatus]);

  const openCreate = () => {
    setForm({ storeId: '', agreedPercentage: 10, validityPeriodMonths: 6, merchantEmail: '' });
    setModalOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/config/admin/agreements', form);
      toast.success('Agreement created');
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    } finally { setSaving(false); }
  };

  const handleSend = async (id) => {
    setSending(id);
    try {
      const res = await api.post(`/api/config/admin/agreements/${id}/send`);
      if (res.data.recipients?.length > 0) {
        toast.success('Agreement sent to merchant via email');
      } else {
        toast.info('SMTP not configured or no recipients found.');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally { setSending(null); }
  };

  const handleMallSign = async (id) => {
    try {
      await api.put(`/api/config/admin/agreements/${id}/mall-sign`);
      toast.success('Signed by Mall Management');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this agreement?')) return;
    try {
      await api.delete(`/api/config/admin/agreements/${id}`);
      toast.success('Agreement deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const startEditEmail = (agr) => {
    setEditingEmail(agr.id);
    setEditEmailValue(agr.merchantEmail || '');
  };

  const saveEmail = async (id) => {
    if (!editEmailValue.trim()) { toast.error('Email is required'); return; }
    setSavingEmail(true);
    try {
      await api.put(`/api/config/admin/agreements/${id}`, { merchantEmail: editEmailValue.trim() });
      toast.success('Merchant email updated');
      setEditingEmail(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally { setSavingEmail(false); }
  };

  const { formatShortDate: formatDate } = useCurrency();

  const handleDownloadPdf = async (id) => {
    setPdfLoading(id + '-dl');
    try {
      const res = await api.get(`/api/config/admin/agreements/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `agreement_${id}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
    finally { setPdfLoading(null); }
  };

  const handleSendPdf = async (id) => {
    setPdfLoading(id + '-send');
    try {
      const res = await api.post(`/api/config/admin/agreements/${id}/send-pdf`);
      toast.success(res.data.message || 'PDF email sent');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to send PDF'); }
    finally { setPdfLoading(null); }
  };

  return (
    <div data-testid="agreements-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none" data-testid="filter-agreement-status">
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="pending_signature">Pending Signature</option>
            <option value="active">Active</option>
          </select>
          <span className="text-sm text-gray-400">{agreements.length} agreement(s)</span>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-[#EC1A23] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#C4121A]" data-testid="create-agreement-btn">
          <Plus size={15} /> New Agreement
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : agreements.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText size={40} className="mx-auto mb-3 text-gray-300" />
          <p>No agreements yet. Create one to invite stores to the voucher program.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {agreements.map(agr => {
            const sc = statusConfig[agr.status] || statusConfig.draft;
            const StatusIcon = sc.icon;
            return (
              <div key={agr.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="agreement-card">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-[#561D1A] truncate">{agr.merchantName}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${sc.color}`}>
                        <StatusIcon size={11} /> {sc.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                      <span>No: <span className="font-mono text-gray-700">{agr.agreementNumber}</span></span>
                      <span>Commission: <strong>{agr.agreedPercentage}%</strong></span>
                      <span>{formatDate(agr.agreementStartDate)} — {formatDate(agr.agreementEndDate)}</span>
                      <span className="flex items-center gap-1">
                        Email:{' '}
                        {editingEmail === agr.id ? (
                          <span className="inline-flex items-center gap-1">
                            <input
                              type="email"
                              value={editEmailValue}
                              onChange={e => setEditEmailValue(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && saveEmail(agr.id)}
                              className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-48 outline-none focus:border-[#561D1A]"
                              autoFocus
                              data-testid="edit-email-input"
                            />
                            <button onClick={() => saveEmail(agr.id)} disabled={savingEmail}
                              className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50" data-testid="save-email-btn">
                              <Check size={13} />
                            </button>
                            <button onClick={() => setEditingEmail(null)} className="text-gray-400 hover:text-gray-600">
                              <X size={13} />
                            </button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <strong className="text-gray-700">{agr.merchantEmail || 'Not set'}</strong>
                            <button onClick={() => startEditEmail(agr)} className="text-gray-400 hover:text-[#561D1A]" data-testid="edit-email-btn">
                              <Pencil size={11} />
                            </button>
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className={agr.mallSignedAt ? 'text-emerald-600' : 'text-gray-400'}>
                        Mall: {agr.mallSignedAt ? 'Signed' : 'Not signed'}
                      </span>
                      <span className={agr.merchantSignedAt ? 'text-emerald-600' : 'text-gray-400'}>
                        Merchant: {agr.merchantSignedAt ? 'Signed' : 'Not signed'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => handleDownloadPdf(agr.id)} disabled={pdfLoading === agr.id + '-dl'}
                      className="text-xs bg-gray-50 text-gray-600 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 flex items-center gap-1 disabled:opacity-50" data-testid="download-pdf-btn" title="Download secured PDF">
                      <Download size={11} /> PDF
                    </button>
                    {agr.merchantEmail && (
                      <button onClick={() => handleSendPdf(agr.id)} disabled={pdfLoading === agr.id + '-send'}
                        className="text-xs bg-purple-50 text-purple-600 px-2.5 py-1.5 rounded-lg hover:bg-purple-100 flex items-center gap-1 disabled:opacity-50" data-testid="send-pdf-btn" title="Email PDF to store owner">
                        <Mail size={11} /> {pdfLoading === agr.id + '-send' ? '...' : 'Email PDF'}
                      </button>
                    )}
                    {!agr.mallSignedAt && (
                      <button onClick={() => handleMallSign(agr.id)} className="text-xs bg-[#561D1A] text-white px-3 py-1.5 rounded-lg hover:bg-[#3E1412]" data-testid="mall-sign-btn">
                        Mall Sign
                      </button>
                    )}
                    {!agr.merchantSignedAt && (
                      <button onClick={() => handleSend(agr.id)} disabled={sending === agr.id}
                        className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-100 flex items-center gap-1 disabled:opacity-50" data-testid="send-agreement-btn">
                        <Send size={11} /> {sending === agr.id ? 'Sending...' : 'Send'}
                      </button>
                    )}
                    <button onClick={() => handleDelete(agr.id)} className="text-xs text-red-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50" data-testid="delete-agreement-btn">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <Modal title="Create Store Agreement" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Store *</label>
              <select required value={form.storeId} onChange={e => setForm(f => ({...f, storeId: e.target.value}))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="agreement-store-select">
                <option value="">Select store...</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Commission %</label>
                <input type="number" step="0.5" min="0" max="50" value={form.agreedPercentage}
                  onChange={e => setForm(f => ({...f, agreedPercentage: parseFloat(e.target.value) || 10}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="commission-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Validity (months)</label>
                <input type="number" min="1" max="36" value={form.validityPeriodMonths}
                  onChange={e => setForm(f => ({...f, validityPeriodMonths: parseInt(e.target.value) || 6}))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Merchant Contact Email *</label>
              <input required type="email" value={form.merchantEmail}
                onChange={e => setForm(f => ({...f, merchantEmail: e.target.value}))}
                placeholder="store-owner@example.com"
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="merchant-email-input" />
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500">
              The agreement will be sent to the merchant for digital signing. They'll receive an email with a link to review and sign the agreement.
            </div>
            <button type="submit" disabled={saving} className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium" data-testid="save-agreement-btn">
              {saving ? 'Creating...' : 'Create Agreement'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
